// The codeless dashboard canvas, and the read-only view of what it produces.
//
// **The state shape, in one paragraph.** The page holds ONE `StudioDoc` — which
// is the wire shape, `{spec, charts}`, exactly as `PUT /mine/dashboards/{id}`
// takes it — plus the version it was loaded at, plus a selection. Every edit is
// a pure function from that document to the next one (`lib/studio-model.ts`,
// `lib/canvas-layout.ts`, `lib/chart-edit.ts`); nothing is stored twice and
// nothing is translated on save. `dirty` is a JSON comparison against the last
// document the server acknowledged, which is cheap at this size and cannot
// drift from the truth the way a manually-set flag does.
//
// **Undo falls out of that shape.** Because every edit is a pure document
// transform, history is a list of documents (`lib/doc-history.ts`) rather than a
// set of inverse operations — so a new kind of edit cannot forget to implement
// its own undo. History RESTARTS at every document the server acknowledged: a
// save, a reload, a restore or a fork, because undoing through one of those
// would silently re-introduce edits somebody had just discarded.
//
// **A canvas starts from an experiment, not from nothing.** `/studio/new`
// carrying `?experiment=` seeds a whole starting board (`lib/studio-starter.ts`)
// scoped to that key: the control bound, the parameter set so the board is a
// template from its first save, and the five tiles a person building an
// experiment readout builds anyway. A blank grid is a worse product than a good
// default, and it makes the first question "which of these six views has the arm
// rates" rather than "did the arm move the number".
//
// **Why the editor previews per tile and the viewer queries the board.** A
// canvas is mostly full of specs nobody has stored, so there is no row to query
// and each tile asks for itself through `POST /mine/preview`. A saved dashboard
// is one `POST /mine/dashboards/{id}/query` for everything on it, on the same
// fan-out, cache, budget and Pusher settle as an authored dashboard. Two data
// paths, one `ChartCard`, one `useBiDashboardQuery`.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  BiSource,
  ChartSpec,
  FilterState,
  NativeFilterSpec,
  StudioDashboardResponse,
  StudioVersionEntry,
} from "@bi/shared/types";

import { Button } from "@bi/shared";

import { CanvasGrid } from "../components/studio/canvas-grid";
import { FieldRail } from "../components/studio/field-rail";
import { PropertyPanel } from "../components/studio/property-panel";
import { SaveBar } from "../components/studio/save-bar";
import { StudioDashboardView } from "../components/studio/studio-dashboard-view";
import { EmptyState, ErrorState, LoadingState, errorMessage } from "../components/states";
import { EMPLOYEE_EMAIL } from "../config";
import {
  ApiError,
  createStudioDashboard,
  deleteStudioDashboard,
  exportStudioDashboard,
  forkStudioDashboard,
  restoreStudioVersion,
  shareStudioDashboard,
  updateStudioDashboard,
} from "../lib/api";
import { type TileAddress, selectionAfterRemoval } from "../lib/canvas-layout";
import { applyField } from "../lib/chart-edit";
import {
  type History,
  canRedo,
  canUndo,
  historyGesture,
  newHistory,
  pushHistory,
  redo,
  resetHistory,
  undo,
} from "../lib/doc-history";
import {
  boundExperiment,
  coverageFor,
  coveredSourceIds,
  experimentFilterState,
  isExperimentTemplate,
  rebindExperiment,
} from "../lib/experiment-scope";
import { defaultFilterState } from "../lib/filter-state";
import {
  biSourceQuery,
  biSourcesQuery,
  experimentsQuery,
  studioDashboardQuery,
  studioDashboardsQuery,
  studioKeys,
  studioVersionsQuery,
} from "../lib/queries";
import {
  type StudioDoc,
  type TileKind,
  addChart,
  addControl,
  addMarkdown,
  applyTileKind,
  chartAt,
  defaultChartForKind,
  duplicateTileAt,
  isMarkdownTile,
  newControl,
  newStudioDoc,
  placedChartIds,
  putChart,
  putControl,
  putParameter,
  removeControl,
  removeParameter,
  removeTileAt,
  setMarkdown,
  setTitle,
  sourceIdsOf,
} from "../lib/studio-model";
import { STARTER_RECIPES, starterDoc } from "../lib/studio-starter";
import { useStudioDashboardCharts } from "../lib/studio-query";
import { explainEmptyTile } from "../lib/tile-empty";

type Selection =
  | { kind: "tile"; address: TileAddress }
  | { kind: "control"; id: string }
  | null;

export interface StudioPageProps {
  /** `null` for `/studio/new`. */
  dashboardId: string | null;
  edit: boolean;
  /** From the URL. On `/studio/new` it SEEDS a starter board for that
   *  experiment; on `/studio/$id` it REBINDS the stored board to read that
   *  experiment, which is the link form of "reopen this against another one". */
  seedExperiment?: string | null;
  /** The source the rail should open on, and a field to bring into the first
   *  tile. Both come from the search box, so a hit on a column lands somewhere
   *  it can be used rather than somewhere it can be read. */
  seedSource?: string | null;
  seedField?: string | null;
}

export function StudioPage({
  dashboardId,
  edit,
  seedExperiment = null,
  seedSource = null,
  seedField = null,
}: StudioPageProps) {
  const stored = useQuery({ ...studioDashboardQuery(dashboardId ?? ""), enabled: Boolean(dashboardId) });
  if (dashboardId && stored.isPending) return <LoadingState label="Loading dashboard…" />;
  if (dashboardId && stored.isError) {
    const status = stored.error instanceof ApiError ? stored.error.status : undefined;
    return (
      <ErrorState
        error={stored.error}
        title={status === 404 ? "This dashboard doesn't exist" : "Couldn't load this dashboard"}
        onRetry={() => void stored.refetch()}
      />
    );
  }
  // Remounted per dashboard so the seeded document below cannot survive a
  // navigation between two of them — the bug that shape produces is an editor
  // showing one dashboard's tiles under another's title.
  return (
    <StudioEditor
      key={`${dashboardId ?? "new"}:${seedExperiment ?? ""}`}
      dashboardId={dashboardId}
      edit={edit}
      stored={stored.data ?? null}
      seedExperiment={seedExperiment}
      seedSource={seedSource}
      seedField={seedField}
    />
  );
}

function StudioEditor({
  dashboardId: initialId,
  edit,
  stored,
  seedExperiment,
  seedSource,
  seedField,
}: {
  dashboardId: string | null;
  edit: boolean;
  stored: StudioDashboardResponse | null;
  seedExperiment: string | null;
  seedSource: string | null;
  seedField: string | null;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sourcesList = useQuery(biSourcesQuery);
  const sources = useMemo(() => sourcesList.data?.sources ?? [], [sourcesList.data]);
  const register = useQuery(experimentsQuery);

  const [dashboardId, setDashboardId] = useState(initialId);
  const [history, setHistory] = useState<History<StudioDoc>>(() =>
    newHistory(stored?.spec ?? newStudioDoc("Untitled dashboard")),
  );
  const doc = history.present;
  const [version, setVersion] = useState(stored?.version ?? 0);
  const [visibility, setVisibility] = useState(stored?.visibility ?? "private");
  const [selection, setSelection] = useState<Selection>(null);
  const [conflict, setConflict] = useState<{ currentVersion: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [exportFiles, setExportFiles] = useState<Record<string, string> | null>(null);
  const [filters, setFilters] = useState<FilterState>(() => defaultFilterState());
  const [starterSkipped, setStarterSkipped] = useState<string[]>([]);
  const [experimentKey, setExperimentKey] = useState<string | null>(
    stored?.experimentKey ?? seedExperiment ?? null,
  );
  // The last document the SERVER acknowledged. `dirty` is a comparison against
  // this rather than a flag somebody has to remember to set, so no edit path
  // can forget to mark itself.
  const savedRef = useRef(JSON.stringify(stored?.spec ?? null));
  const isOwner = stored?.isOwner ?? true;
  const employeeEmail = stored?.ownerEmail || EMPLOYEE_EMAIL;
  const dirty = JSON.stringify(doc) !== savedRef.current;

  /** Record an edit. Every edit path goes through here, which is what makes the
   *  undo stack complete by construction rather than by discipline. */
  const setDoc = useCallback((next: (current: StudioDoc) => StudioDoc) => {
    setHistory((h) => pushHistory(h, next(h.present), (a, b) => JSON.stringify(a) === JSON.stringify(b)));
  }, []);

  // The full detail of every source the document reads, keyed by id. Needed
  // beyond the selected tile's own: an annotation editor offers the EVENTS
  // source's columns, which belong to a source no tile is selected on.
  const [pickedSourceId, setPickedSourceId] = useState<string>(seedSource ?? "");
  const neededSourceIds = useMemo(() => {
    const ids = new Set(sourceIdsOf(doc));
    if (pickedSourceId) ids.add(pickedSourceId);
    for (const s of sources) ids.add(s.id);
    return [...ids];
  }, [doc, pickedSourceId, sources]);
  const sourceDetails = useQueries({
    queries: neededSourceIds.map((id) => biSourceQuery(id)),
  });
  const sourcesById = useMemo(() => {
    const map: Record<string, BiSource> = {};
    for (const q of sourceDetails) {
      if (q.data) map[q.data.source.id] = q.data.source;
    }
    return map;
  }, [sourceDetails]);
  // The PHYSICAL dataset uuid per source, as the server states it. Never
  // recomputed here: `compile_studio_chart` resolves a built chart's dataset by
  // this key, and a client that derives it is a second copy of an identity rule
  // whose disagreement shows up as every chart failing to resolve a dataset
  // that exists. A source whose detail has not landed yet simply cannot have a
  // chart minted over it, which `addTile` reports rather than guessing past.
  const datasetUuidBySource = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of sources) map[s.id] = s.datasetUuid;
    for (const q of sourceDetails) {
      if (q.data) map[q.data.source.id] = q.data.datasetUuid;
    }
    return map;
  }, [sources, sourceDetails]);

  const coverage = useMemo(
    () => (experimentKey ? coverageFor(register.data?.experiments ?? [], experimentKey) : []),
    [register.data, experimentKey],
  );
  const covered = useMemo(() => coveredSourceIds(coverage), [coverage]);
  const registerKnown = register.data !== undefined;

  const versions = useQuery(studioVersionsQuery(dashboardId ?? "", versionsOpen && Boolean(dashboardId)));

  // ── the starter board ─────────────────────────────────────────────────────
  //
  // Once, when everything it needs has landed: the source list, every starter
  // source's detail (for the column check) and the register (for which sources
  // carry this experiment). Guarded by a ref rather than by a state flag so a
  // re-render mid-load cannot seed twice and double the tiles.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || initialId || !seedExperiment) return;
    if (sources.length === 0 || register.isPending) return;
    const wanted = [...new Set(STARTER_RECIPES.map((r) => r.sourceId))].filter((id) =>
      sources.some((s) => s.id === id),
    );
    if (!wanted.every((id) => sourcesById[id])) return;
    seededRef.current = true;
    const seed = starterDoc(
      seedExperiment,
      coverageFor(register.data?.experiments ?? [], seedExperiment),
      sourcesById,
      datasetUuidBySource,
    );
    setHistory(resetHistory(seed.doc));
    setFilters(experimentFilterState(seed.doc, seedExperiment).filters);
    setStarterSkipped(seed.skipped);
  }, [
    initialId,
    seedExperiment,
    sources,
    sourcesById,
    datasetUuidBySource,
    register.isPending,
    register.data,
  ]);

  // ── reopening a SAVED board against another experiment ────────────────────
  //
  // A rebind, not a copy: one control's default and one parameter's default
  // move together, and the tiles are the same tiles. Applied once on open so a
  // later hand-edit of the control is not fought by this effect.
  const reboundRef = useRef(false);
  useEffect(() => {
    if (reboundRef.current || !initialId || !seedExperiment) return;
    reboundRef.current = true;
    setHistory((h) => resetHistory(rebindExperiment(h.present, seedExperiment)));
  }, [initialId, seedExperiment]);

  // Whatever the document is bound to now, the live filter state follows it.
  const boundKey = boundExperiment(doc);
  const boundRef = useRef<string | null>(null);
  useEffect(() => {
    if (boundKey === boundRef.current) return;
    boundRef.current = boundKey;
    if (!boundKey) return;
    setFilters((current) => experimentFilterState(doc, boundKey, current).filters);
    setExperimentKey((current) => current ?? boundKey);
    // `doc` is deliberately not a dependency: this reacts to the BOUND KEY
    // changing, and re-running it on every keystroke of a chart title would
    // reset a reader's own filter selections under them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundKey]);

  const selectedChart = selection?.kind === "tile" ? (chartAt(doc, selection.address) ?? null) : null;
  const selectedTile =
    selection?.kind === "tile"
      ? (doc.spec.layout.rows[selection.address.row]?.tiles[selection.address.index] ?? null)
      : null;
  const selectedControl =
    selection?.kind === "control"
      ? (doc.spec.filters.find((f) => f.id === selection.id) ?? null)
      : null;
  const activeSourceId = (selectedChart?.source_id ?? pickedSourceId) || sources[0]?.id || "";
  const activeSource = sourcesById[activeSourceId] ?? null;

  const updateChart = (chart: ChartSpec) => setDoc((d) => putChart(d, chart));

  const addTile = (kind: TileKind) => {
    const source = activeSource ?? Object.values(sourcesById)[0];
    const datasetUuid = source ? datasetUuidBySource[source.id] : undefined;
    if (!source || !datasetUuid) {
      setError("Pick a data source first. This environment has none to build over yet.");
      return;
    }
    let chart = defaultChartForKind(kind, source, datasetUuid);
    // A field carried in from the search box goes straight into the tile it
    // opened, so a hit on a column ends in a chart rather than in a rail.
    const field = seedField ? source.columns?.find((c) => c.name === seedField) : undefined;
    if (field) chart = applyField(chart, field);
    setDoc((d) => addChart(d, chart));
    setSelection({ kind: "tile", address: { row: doc.spec.layout.rows.length, index: 0 } });
  };

  const changeKind = (kind: TileKind) => {
    if (!selectedChart || !activeSource) return;
    updateChart(applyTileKind(selectedChart, kind, activeSource));
  };

  /** Re-point the selected tile at a different source.
   *
   *  A rebuild, not a re-label: every column name on the chart was checked
   *  against the OLD source's schema, so carrying the groupby and metrics over
   *  would produce a chart naming columns the new source does not declare —
   *  which the validator refuses and, worse, refuses one field at a time. */
  const changeSource = (sourceId: string) => {
    setPickedSourceId(sourceId);
    const source = sourcesById[sourceId];
    const datasetUuid = datasetUuidBySource[sourceId];
    if (!selectedChart || !source || !datasetUuid) return;
    const rebuilt = defaultChartForKind(
      // Keep the SHAPE the person chose; only the data behind it moves.
      selectedChart.viz_type === "table" ? "table" : "bar",
      source,
      datasetUuid,
      { uuid: selectedChart.uuid, title: selectedChart.slice_name },
    );
    updateChart({
      ...rebuilt,
      viz_type: selectedChart.viz_type,
      description: selectedChart.description ?? null,
    });
  };

  const deleteSelection = () => {
    if (selection?.kind === "control") {
      setDoc((d) => removeControl(d, selection.id));
      setSelection(null);
      return;
    }
    if (selection?.kind !== "tile") return;
    const next = selectionAfterRemoval(doc.spec.layout, selection.address);
    setDoc((d) => removeTileAt(d, selection.address));
    setSelection(next ? { kind: "tile", address: next } : null);
  };

  const duplicateSelection =
    selection?.kind === "tile"
      ? () => {
          const address = selection.address;
          setDoc((d) => duplicateTileAt(d, address));
          setSelection({ kind: "tile", address: { row: address.row, index: address.index + 1 } });
        }
      : null;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const gesture = historyGesture(event);
      if (!gesture) return;
      event.preventDefault();
      setHistory((h) => (gesture === "undo" ? undo(h) : redo(h)));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const acknowledge = (resp: StudioDashboardResponse, { navigateTo = false } = {}) => {
    setDashboardId(resp.id);
    // History RESTARTS here. Undoing back through a save would put edits the
    // server has already rejected or replaced back on screen as if they were
    // current, which is worse than having no undo at all.
    setHistory(resetHistory(resp.spec));
    setVersion(resp.version);
    setVisibility(resp.visibility);
    setExperimentKey(resp.experimentKey ?? null);
    savedRef.current = JSON.stringify(resp.spec);
    setConflict(null);
    setError(null);
    void queryClient.invalidateQueries({ queryKey: studioDashboardsQuery.queryKey });
    queryClient.setQueryData(studioKeys.dashboard(resp.id), resp);
    if (navigateTo) {
      void navigate({ to: "/studio/$id", params: { id: resp.id }, search: { edit: "1" } });
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!dashboardId) {
        return createStudioDashboard({
          title: doc.spec.title,
          spec: doc,
          visibility: "private",
          experimentKey: experimentKey ?? boundExperiment(doc),
        });
      }
      return updateStudioDashboard(dashboardId, {
        title: doc.spec.title,
        spec: doc,
        version,
        experimentKey: experimentKey ?? boundExperiment(doc),
      });
    },
    onSuccess: (resp) => acknowledge(resp, { navigateTo: !dashboardId }),
    onError: (err: unknown) => {
      // A 409 is NOT an error to retry through. The server bumped nothing; the
      // stored version simply moved, and re-issuing the save with that version
      // would be last-write-wins wearing a check. The bar offers the two things
      // a person can actually mean instead.
      if (err instanceof ApiError && err.status === 409) {
        const current = Number(err.payload?.current_version ?? version);
        setConflict({ currentVersion: Number.isFinite(current) ? current : version });
        return;
      }
      setError(errorMessage(err));
    },
  });

  const saveAsCopy = useMutation({
    mutationFn: () =>
      createStudioDashboard({
        title: `${doc.spec.title} (my copy)`,
        spec: doc,
        visibility: "private",
        experimentKey: experimentKey ?? boundExperiment(doc),
      }),
    onSuccess: (resp) => acknowledge(resp, { navigateTo: true }),
    onError: (err: unknown) => setError(errorMessage(err)),
  });

  const share = useMutation({
    mutationFn: (next: boolean) =>
      shareStudioDashboard(dashboardId ?? "", { visibility: next ? "shared" : "private" }),
    onSuccess: (resp) => acknowledge(resp),
    onError: (err: unknown) => setError(errorMessage(err)),
  });

  const fork = useMutation({
    mutationFn: () => forkStudioDashboard(dashboardId ?? "", {}),
    onSuccess: (resp) => acknowledge(resp, { navigateTo: true }),
    onError: (err: unknown) => setError(errorMessage(err)),
  });

  const restore = useMutation({
    mutationFn: (target: number) => restoreStudioVersion(dashboardId ?? "", target),
    onSuccess: (resp) => {
      acknowledge(resp);
      void queryClient.invalidateQueries({ queryKey: studioKeys.versions(resp.id) });
    },
    onError: (err: unknown) => setError(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: () => deleteStudioDashboard(dashboardId ?? ""),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: studioDashboardsQuery.queryKey });
      void navigate({ to: "/studio" });
    },
    onError: (err: unknown) => setError(errorMessage(err)),
  });

  const exportYaml = useMutation({
    mutationFn: () => exportStudioDashboard(dashboardId ?? ""),
    onSuccess: (resp) => setExportFiles(resp.files),
    onError: (err: unknown) => setError(errorMessage(err)),
  });

  const reloadSaved = () => {
    void queryClient
      .fetchQuery(studioDashboardQuery(dashboardId ?? ""))
      .then((resp) => acknowledge(resp))
      .catch((err: unknown) => setError(errorMessage(err)));
  };

  const explainEmpty = useCallback(
    (chart: ChartSpec) =>
      explainEmptyTile({
        chart,
        filters,
        experimentKey: boundKey ?? experimentKey,
        coveredSourceIds: registerKnown ? covered : null,
      }),
    [filters, boundKey, experimentKey, registerKnown, covered],
  );

  // A dashboard that cannot be edited by this reader, or that they asked to
  // view, renders read-only through the saved-dashboard query path.
  if (!edit || !isOwner) {
    return (
      <StudioReadOnly
        stored={stored}
        doc={doc}
        canEdit={isOwner}
        initialFilters={
          boundKey ? experimentFilterState(doc, boundKey).filters : defaultFilterState()
        }
        explainEmpty={explainEmpty}
        onEdit={() =>
          void navigate({
            to: "/studio/$id",
            params: { id: dashboardId ?? "" },
            search: { edit: "1" },
          })
        }
        onFork={() => fork.mutate()}
      />
    );
  }

  return (
    <div className="bi-page bi-studio">
      <SaveBar
        title={doc.spec.title}
        onTitleChange={(title) => setDoc((d) => setTitle(d, title))}
        dashboardId={dashboardId}
        isOwner={isOwner}
        shared={visibility === "shared"}
        dirty={dirty}
        saving={save.isPending}
        version={version}
        onAddTile={addTile}
        onAddText={() => {
          setDoc((d) => addMarkdown(d, "## A heading\n\nSay what this section shows."));
          setSelection({ kind: "tile", address: { row: doc.spec.layout.rows.length, index: 0 } });
        }}
        onAddControl={() => {
          const control = newControl("select");
          setDoc((d) => addControl(d, control));
          setSelection({ kind: "control", id: control.id });
        }}
        canUndo={canUndo(history)}
        canRedo={canRedo(history)}
        onUndo={() => setHistory((h) => undo(h))}
        onRedo={() => setHistory((h) => redo(h))}
        onDuplicateTile={duplicateSelection}
        experiment={
          isExperimentTemplate(doc)
            ? {
                key: boundKey,
                options: (register.data?.experiments ?? []).map((e) => e.key),
                onRebind: (key) => setDoc((d) => rebindExperiment(d, key)),
              }
            : null
        }
        onSave={() => save.mutate()}
        onShareChange={(next) => share.mutate(next)}
        onFork={() => fork.mutate()}
        onDelete={() => {
          if (window.confirm("Delete this dashboard? Anyone it is shared with loses it too.")) {
            remove.mutate();
          }
        }}
        onExport={() => exportYaml.mutate()}
        onToggleVersions={() => setVersionsOpen((open) => !open)}
        versionsOpen={versionsOpen}
        versions={(versions.data?.versions ?? []) as StudioVersionEntry[]}
        onRestore={(target) => restore.mutate(target)}
        conflict={conflict}
        onReloadSaved={reloadSaved}
        onSaveAsCopy={() => saveAsCopy.mutate()}
        exportFiles={exportFiles}
        onDismissExport={() => setExportFiles(null)}
        error={error}
      />

      <StarterNote skipped={starterSkipped} experimentKey={seedExperiment} />

      <div className="bi-studio__body">
        <FieldRail
          sources={sources}
          source={activeSource}
          selectedSourceId={activeSourceId}
          onSelectSource={changeSource}
          onUseField={
            selectedChart
              ? (column) => updateChart(applyField(selectedChart, column))
              : undefined
          }
          syncedAt={sourcesList.data?.syncedAt ?? null}
          experimentKey={boundKey ?? experimentKey}
          coveredSourceIds={registerKnown ? covered : null}
        />

        <div className="bi-studio__centre">
          {doc.spec.filters.length > 0 ? (
            <div className="bi-studio-controls" aria-label="Controls">
              {doc.spec.filters.map((control) => (
                <button
                  key={control.id}
                  type="button"
                  className={`bi-studio-control-chip${
                    selection?.kind === "control" && selection.id === control.id
                      ? " bi-studio-control-chip--selected"
                      : ""
                  }`}
                  onClick={() => setSelection({ kind: "control", id: control.id })}
                >
                  {control.name}
                </button>
              ))}
            </div>
          ) : null}
          <CanvasGrid
            layout={doc.spec.layout}
            charts={doc.charts ?? {}}
            datasets={datasetsFor(doc)}
            filters={filters}
            employeeEmail={employeeEmail}
            selected={selection?.kind === "tile" ? selection.address : null}
            onSelect={(address) =>
              setSelection(address ? { kind: "tile", address } : null)
            }
            onLayoutChange={(layout, nextSelected) => {
              setDoc((d) => ({ ...d, spec: { ...d.spec, layout } }));
              setSelection(nextSelected ? { kind: "tile", address: nextSelected } : null);
            }}
            emptyExplain={explainEmpty}
          />
        </div>

        <PropertyPanel
          chart={selectedChart}
          source={selectedChart ? (sourcesById[selectedChart.source_id ?? ""] ?? null) : null}
          sources={sources}
          sourcesById={sourcesById}
          onChartChange={updateChart}
          onSourceChange={changeSource}
          onKindChange={changeKind}
          onDelete={deleteSelection}
          markdown={
            selectedTile && isMarkdownTile(selectedTile) && selection?.kind === "tile"
              ? {
                  value: selectedTile.markdown,
                  onChange: (next) =>
                    setDoc((d) => setMarkdown(d, (selection as { address: TileAddress }).address, next)),
                }
              : null
          }
          control={
            selectedControl
              ? {
                  control: selectedControl,
                  onChange: (next: NativeFilterSpec) => setDoc((d) => putControl(d, next)),
                  onDelete: deleteSelection,
                  parameterName:
                    (doc.spec.parameters ?? []).find((p) => p.filter_id === selectedControl.id)
                      ?.name ?? null,
                  onParameterChange: (name) =>
                    setDoc((d) => {
                      const existing = (d.spec.parameters ?? []).find(
                        (p) => p.filter_id === selectedControl.id,
                      );
                      if (!name) return existing ? removeParameter(d, existing.name) : d;
                      const cleared = existing ? removeParameter(d, existing.name) : d;
                      return putParameter(cleared, {
                        name,
                        filter_id: selectedControl.id,
                        default: null,
                      });
                    }),
                }
              : null
          }
        />
      </div>

      {/* The canvas's own date range. Local rather than the shared filter
          store: an editor changing a window is composing, not navigating, and
          leaking that into the store would change what the NEXT dashboard the
          reader opens shows. */}
      <FiltersNote filters={filters} onReset={() => setFilters(defaultFilterState())} />
    </div>
  );
}

/** What the starter board could NOT lay down, and why.
 *
 *  Reported rather than swallowed: a board that is two tiles short should say
 *  which two, or somebody concludes the experiment has no funnel rather than
 *  that this environment's warehouse has no funnel view yet. */
function StarterNote({
  skipped,
  experimentKey,
}: {
  skipped: readonly string[];
  experimentKey: string | null;
}) {
  if (skipped.length === 0 || !experimentKey) return null;
  return (
    <p className="bi-studio-rail__note">
      {skipped.length} starter tile{skipped.length === 1 ? " was" : "s were"} left out (
      {skipped.join(", ")}): this environment's warehouse either has no view for{" "}
      {experimentKey} there, or the view does not declare the columns the tile reads. Add them
      by hand from the rail if you know better.
    </p>
  );
}

function FiltersNote({ filters, onReset }: { filters: FilterState; onReset: () => void }) {
  if (!filters.time_range_start && !filters.time_range_end) return null;
  return (
    <p className="bi-studio-field__hint">
      Previews cover {filters.time_range_start} to {filters.time_range_end}.{" "}
      <button type="button" className="bi-studio-linkish" onClick={onReset}>
        Reset
      </button>
    </p>
  );
}

/** The physical datasets the tiles read, keyed as `ChartCard` expects.
 *
 *  Keyed off each chart's OWN `dataset_uuid` rather than taken from a response,
 *  so it is correct for an UNSAVED dashboard too — a chart carries the uuid the
 *  server stated for its source from the moment it is minted. Only the fields
 *  the card actually reads are filled: it uses a dataset to decide
 *  drag-to-zoom eligibility and to scope a filter, both of which key on
 *  columns. */
function datasetsFor(doc: StudioDoc) {
  const datasets: Record<string, ReturnType<typeof emptyDataset>> = {};
  for (const chart of Object.values(doc.charts ?? {})) {
    if (!chart.source_id) continue;
    datasets[chart.dataset_uuid] = emptyDataset(chart.dataset_uuid, chart.source_id);
  }
  return datasets;
}

function emptyDataset(uuid: string, tableName: string) {
  return {
    uuid,
    table_name: tableName,
    audience: "internal" as const,
    bq_dataset: "",
    bq_project: null,
    columns: [],
    parameters: [],
    sql: null,
    table_ref: null,
    description: null,
    main_dttm_col: null,
    cache_timeout_seconds: null,
    cohort_filters: null,
    extras: {},
  };
}

function StudioReadOnly({
  stored,
  doc,
  canEdit,
  initialFilters,
  explainEmpty,
  onEdit,
  onFork,
}: {
  stored: StudioDashboardResponse | null;
  doc: StudioDoc;
  canEdit: boolean;
  initialFilters: FilterState;
  explainEmpty: (chart: ChartSpec) => string;
  onEdit: () => void;
  onFork: () => void;
}) {
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const chartIds = useMemo(() => placedChartIds(doc), [doc]);
  const { charts: chartStates, annotationIds, retryChart } = useStudioDashboardCharts(
    stored?.id ?? "",
    doc.spec.uuid,
    chartIds,
    filters,
    stored?.ownerEmail || EMPLOYEE_EMAIL,
  );

  if (!stored) return <EmptyState title="Nothing to show yet" message="Save this dashboard first." />;

  return (
    <div className="bi-page">
      <div className="bi-page__head">
        <h1 className="bi-page__title">{doc.spec.title}</h1>
        {canEdit ? (
          <Button variant="outline" tone="neutral" size="sm" onClick={onEdit}>
            Edit
          </Button>
        ) : (
          <Button variant="outline" tone="neutral" size="sm" onClick={onFork}>
            Fork to my own copy
          </Button>
        )}
      </div>
      <StudioDashboardView
        layout={doc.spec.layout}
        controls={doc.spec.filters}
        charts={doc.charts ?? {}}
        datasets={stored.datasets}
        chartStates={chartStates}
        annotationIds={annotationIds}
        filters={filters}
        onFiltersChange={setFilters}
        employeeEmail={stored.ownerEmail || EMPLOYEE_EMAIL}
        emptyExplain={explainEmpty}
        onRetry={retryChart}
      />
    </div>
  );
}
